import { ArrowUpRight, CornerDownRight } from '@untitled-ui/icons-react/build/cjs';
import React from 'react';
import ButtonCmp from 'src/components/ButtonCmp';
import LiveElapsedTimer from 'src/components/LiveElapsedTimer';

import AiActionNotification from './AiActionNotification';
import AiTaskHeader, { type AiTaskAgent, type AiTaskStatus } from './AiTaskHeader';
import AiWorkflow, { type Step } from './CommonComponents/AiWorkflow';
import DividerNotification from './DividerNotification';
import ShipmentReviewCard, { type IShipmentReviewFields } from './ShipmentReviewCard';

type AgentMessageContent =
    | { type: 'workflow'; steps: Step[]; defaultExpanded?: boolean; group?: string }
    | { type: 'notification'; text: string; icon?: React.ReactNode }
    | { type: 'taskHeader'; title: string; status: AiTaskStatus; agent?: AiTaskAgent | null; startDate?: string }
    | { type: 'divider'; text: string }
    | { type: 'suggestionChip'; label: string; onClick?: () => void }
    | { type: 'shipmentReview'; fields: IShipmentReviewFields; onMakeChanges: () => void; onCreateShipment: () => void }
    | { type: 'linkMessage'; text: string; linkText: string; onLinkClick?: () => void };

export interface AgentMessage {
    id: string;
    sender: 'user' | 'agent';
    text?: string;
    content?: AgentMessageContent;
}

const AgentMessageBubble = ({ message }: { message: AgentMessage }) => {
    if (message.sender === 'user') {
        return (
            <div className="max-w-[86%] rounded-lg bg-utilityGray100 px-3 py-1.5 ml-auto user-sender animate-slide-in">
                <p className="text-grayLight900 text-xs leading-[1.5] font-normal">{message.text}</p>
            </div>
        );
    }

    if (message.content?.type === 'workflow') {
        const isExpanded = message.content.defaultExpanded ?? true;

        // Keyed on the expand flag so flipping it (e.g. auto-collapsing a completed step once a
        // later step is added) remounts AiWorkflow with fresh internal open/close state — its
        // own useState only reads defaultExpanded on first mount, so a prop change alone wouldn't
        // otherwise re-collapse an already-expanded step.
        return <AiWorkflow key={String(isExpanded)} steps={message.content.steps} defaultExpanded={isExpanded} />;
    }

    if (message.content?.type === 'notification') {
        return <AiActionNotification text={message.content.text} icon={message.content.icon} />;
    }

    if (message.content?.type === 'taskHeader') {
        const { title, status, agent, startDate } = message.content;

        return <AiTaskHeader title={title} status={status} agent={agent} time={<LiveElapsedTimer startDate={startDate} isRunning={status === 'working'} />} />;
    }

    if (message.content?.type === 'divider') {
        return <DividerNotification text={message.content.text} />;
    }

    if (message.content?.type === 'suggestionChip') {
        const { label, onClick } = message.content;

        return (
            <ButtonCmp type="button" className="btn_secondary_black !text-xs self-end w-fit" onClick={onClick}>
                {label}
            </ButtonCmp>
        );
    }

    if (message.content?.type === 'shipmentReview') {
        const { fields, onMakeChanges, onCreateShipment } = message.content;

        return <ShipmentReviewCard fields={fields} onMakeChanges={onMakeChanges} onCreateShipment={onCreateShipment} />;
    }

    if (message.content?.type === 'linkMessage') {
        const { text, linkText, onLinkClick } = message.content;

        return (
            <div className="max-w-full ai-sender space-y-1.5 pb-2 border-b border-utilityGray200">
                <p className="text-gray500 text-xs leading-[1.5] font-normal">{text}</p>
                <button type="button" onClick={onLinkClick} className="flex items-center justify-between w-full text-gray500 text-xs font-medium">
                    <span className="flex items-center gap-x-1">
                        <CornerDownRight className="w-3.5 h-3.5 text-gray400" />
                        <span>{linkText}</span>
                    </span>
                    <ArrowUpRight className="w-3.5 h-3.5 text-primary" />
                </button>
            </div>
        );
    }

    return (
        <div className="max-w-[86%] ai-sender">
            <p className="text-grayLight900 text-xs leading-[1.5] font-normal">{message.text}</p>
        </div>
    );
};

export default AgentMessageBubble;
